import { Grid, IconButton, Menu, MenuItem, Tooltip, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { Email as EmailIcon, Print as PrintIcon, MoreHoriz as MoreHorizIcon, Pageview, Timeline, Delete } from '@material-ui/icons';
import { Clear, FileDownload as FileDownloadIcon, NoteAdd as NoteAddIcon } from '@mui/icons-material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { bindMenu, bindTrigger } from 'material-ui-popup-state';
import _, { isEmpty, isFunction } from 'lodash';

import QCXListItemIcon from '../../shared-components/list-item-icon/QCXListItemIcon';
import QCXSimpleSelectManyPageTemplate from '../../templates/simple-select-many-page/QCXSimpleSelectManyPageTemplate';
import { isAlternativeLoadingStatus, isLoadingStatus } from '../../utils/store/store-utils';
import QCXButton from '../../shared-components/button/QCXButton';
import useDownloader from '../../utils/hooks/downloader/useDownloader';
import { followUpActions } from '../../features/follow-up/followUpSlice';
import useFormDialogSync from '../../utils/hooks/form/dialog/useFormDialogSync';
import QCXCompoundDialog from '../../shared-components/dialog/QCXCompoundDialog';
import { selectStatus } from '../../features/tipo-documento/tipoDocumentoSlice';
import QCXPopupState from '../popup-state/QCXPopupState';
import QCXEmailDocumentoFollowUpFormDialog from '../follow-up/QCXEmailDocumentoFollowUpFormDialog';
import QCXNovoDocumentoClientFormDialog from './QCXNovoDocumentoClientFormDialog';
import QCXInfoAlert from '../../shared-components/alert/QCXInfoAlert';
import { deleteDocumentosByIds, fetchDocumentosByClienteId } from '../../features/cliente/clienteAPI';
import { clienteActions } from '../../features/cliente/clienteSlice';
import { formatDate } from '../../utils/general/general-utils';
import QCXDocumentoClientViewerModal from './QCXDocumentoClientViewerModal';

export default function QCXSelectManyClientDocumentsTable({ model, selected: selectedProp, ...props }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const theme = useTheme();
    const isXsDownScreen = useMediaQuery(theme.breakpoints.down('xs'));
    const status = useSelector(selectStatus);

    const [documentosBuscados, setDocumentosBuscados] = useState([]);

    const [isDeleting, setIsDeleting] = useState(false);

    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const [documentoParaEdicao, setDocumentoParaEdicao] = useState(null);

    const [isEditing, setIsEditing] = useState(false);

    const [documentoEmVisualizacao, setDocumentoEmVisualizacao] = useState(null);

    const [handleOpenModalEdicaoDocumento, formDialogEdicaoDocumentoStatus, handleFormDialogEdicaoDocumentoStatus] =
        useFormDialogSync();

    const handleRefreshDocuments = useCallback(() => {
        setRefreshTrigger(prev => prev + 1);
    }, []);

    const fetchDocuments = useCallback(async (clienteId) => {
        try {
            const response = await fetchDocumentosByClienteId(clienteId);
            if (response?.data) {
                setDocumentosBuscados(response.data);
            }
        } catch (error) {
            console.error("Erro ao buscar documentos do cliente:", error);
        }
    }, []);

    useEffect(() => {
        if (model?.id) {
            fetchDocuments(model.id);
        }
    }, [model?.id, fetchDocuments, refreshTrigger]);

    const documentoArquivoList = useMemo(() => {
        if (Array.isArray(documentosBuscados) && documentosBuscados.length > 0) {
            return documentosBuscados;
        }

        if (Array.isArray(model?.documentos)) return model.documentos;

        if (model?.tipoDocumento || model?.arquivo) {
            return [{
                tipoDocumento: model.tipoDocumento,
                tipo: model.tipo,
                nome: model.nome,
                numero: model.numero,
                notificar: model.notificar,
                arquivo: model.arquivo,
                vencimento: model.dataVencimento,
            }];
        }
        return [];
    }, [model, documentosBuscados]);

    const documentsToDisplay = useMemo(() => (
        documentoArquivoList.map((doc, idx) => ({
            ...doc,
            id: doc.id || doc.arquivo?.id || idx,
            tipoDocumento: doc.tipoDocumento?.description || doc.tipoDocumento?.nomePadrao || '-',
            nome: doc.nome || '-',
            numero: doc.numero || '-',
            vencimento: doc.vencimento || doc.dataVencimento || '-',
            notificar: doc.notificar || false,
        }))
    ), [documentoArquivoList]);

    const [handleOpenModalNovoDocumento, formDialogDocumentoFollowUpStatus, handleFormDialogDocumentoFollowUpStatus] =
        useFormDialogSync();

    const [
        handleOpenModalEnviarDocumentoPorEmail,
        formDialogDocumentoEmailFollowUpStatus,
        handleFormDialogDocumentoEmailFollowUpStatus,
    ] = useFormDialogSync();

    const isAlternativeLoading = useMemo(() => (
        isAlternativeLoadingStatus(status)
    ), [status]);

    const [selectedDocuments, setSelectedDocuments] = useState(selectedProp || []);

    const isMultipleSelection = useMemo(() => selectedDocuments.length > 1, [selectedDocuments]);
    const isSingleSelection = useMemo(() => selectedDocuments.length === 1, [selectedDocuments]);
    const isAnySelection = useMemo(() => selectedDocuments.length >= 1, [selectedDocuments]);

    const downloader = useDownloader({ autoDownload: true });

    const downloading = useMemo(() => isLoadingStatus(downloader.status), [downloader]);

    const {
        title = t('com.muralis.qcx.documento.documentosAdicionadosCliente', { nome: model?.nome }),
        filterProp = 'tipoDocumento,nome,numero,vencimento',
        handleChange,
        isConsult,
    } = props;

    const handleSelectionDocumentsChange = useCallback(
        ({ selectionModel }) => {
            setSelectedDocuments(selectionModel);

            if (_.isFunction(handleChange)) {
                const selectedItems = documentoArquivoList.filter(doc => selectionModel.includes(doc.id));
                handleChange(selectedItems);
            }
        },
        [setSelectedDocuments, handleChange, documentoArquivoList]
    );

    const handleClearDocumentsSelection = useCallback(() => {
        setSelectedDocuments([]);

        if (_.isFunction(handleChange)) {
            handleChange([]);
        }
    }, [setSelectedDocuments, handleChange]);

    useEffect(() => {
        setIsEditing(!isConsult);
    }, [isConsult]);

    const handleDownloadMultipleDocumentFromSelection = useCallback(
        (event) => {
            if (_.isFunction(event?.stopPropagation())) {
                event.stopPropagation();
            }

            if (!_.isEmpty(selectedDocuments)) {
                const filesList = documentoArquivoList
                    .filter((documentoArquivo) => {
                        const selectionId = documentoArquivo?.id || documentoArquivo?.arquivo?.id;

                        return selectedDocuments.includes(selectionId);
                    })
                    .map((documentoArquivo) => {
                        if (!documentoArquivo) return null;

                        const storageObject = documentoArquivo.arquivo || documentoArquivo;

                        return {
                            id: storageObject.id,
                            nome: storageObject.nome || documentoArquivo.nome,
                            uri: storageObject.uri,
                            chave: storageObject.chave,
                            entidadeRelacionada: storageObject.entidadeRelacionada,
                        };
                    })
                    .filter(doc => doc && doc.id);

                if (filesList.length > 0) {
                    downloader.downloadMultipleFilesBy(filesList, handleClearDocumentsSelection);
                } else {
                    console.error("ERRO: Nenhuma ID de arquivo de Storage válida encontrada nos documentos selecionados.");
                }
            }
        }, [downloader, documentoArquivoList, selectedDocuments, handleClearDocumentsSelection]
    );



    const handleOpenSendEmailWithMultipleDocumentFromSelection = useCallback(
        (event) => {
            if (_.isFunction(event?.stopPropagation())) {
                event.stopPropagation();
            }

            if (!_.isEmpty(selectedDocuments)) {
                const filesList = documentoArquivoList.filter((documentoArquivo) => {
                    const selectionId = documentoArquivo?.id || documentoArquivo?.arquivo?.id;
                    return selectedDocuments.includes(selectionId);
                }).map((documentoArquivo) => {
                    if (!documentoArquivo) return null;

                    const storageObject = documentoArquivo.arquivo || documentoArquivo;

                    return {
                        id: storageObject.id,
                        chave: storageObject.chave,
                        nome: storageObject.nome,
                        uri: storageObject.uri,
                    };
                }).filter(doc => doc && (doc.id || doc.chave));

                dispatch(
                    followUpActions.setRelatedEmailModel({
                        id: model?.id,
                        arquivos: filesList,
                    })
                );

                handleOpenModalEnviarDocumentoPorEmail();
            }
        },
        [model, documentoArquivoList, selectedDocuments, handleOpenModalEnviarDocumentoPorEmail]
    );

    const handleDeleteMultipleDocuments = useCallback(
        async (event) => {
            if (_.isFunction(event?.stopPropagation)) {
                event.stopPropagation();
            }

            if (!_.isEmpty(selectedDocuments) && model?.id) {
                setIsDeleting(true);

                const documentIds = selectedDocuments;
                handleClearDocumentsSelection();

                try {

                    await deleteDocumentosByIds(model.id, documentIds);

                    console.log('Documentos desativados/excluídos com sucesso:', documentIds);

                    try {
                        await fetchDocuments(model.id);
                    } catch (cleanupError) {
                        console.error('ERRO PÓS-EXCLUSÃO (Limpeza/Recarregamento):', cleanupError);
                    }

                } catch (error) {
                    console.error('Erro ao desativar/excluir documentos:', error);
                } finally {
                    setIsDeleting(false);
                }
            }
        },
        [selectedDocuments, model?.id, handleClearDocumentsSelection, fetchDocuments]
    );

    const handleConsultDocument = useCallback((row) => {
        const documentoCompleto = documentoArquivoList.find(doc => doc.id === row.id);

        if (documentoCompleto) {
            setDocumentoEmVisualizacao(documentoCompleto);
        } else {
            console.error("Documento completo não encontrado para edição com ID:", row.id);
        }
    }, [documentoArquivoList]);

    const handleCloseConsultModal = useCallback(() => {
        setDocumentoEmVisualizacao(null);
    }, []);

    const handleDeleteDocument = useCallback(
        async (documentoId) => {
            if (documentoId && model?.id) {
                setIsDeleting(true);

                try {
                    await deleteDocumentosByIds(model.id, [documentoId]);

                    handleClearDocumentsSelection();
                    await fetchDocuments(model.id);

                    console.log('Documento excluido com sucesso:', documentoId);
                } catch (error) {
                    console.log('Erro ao excluir documento:', error);
                } finally {
                    setIsDeleting(false);
                }
            }
        },
        [model?.id, handleClearDocumentsSelection, fetchDocuments]
    );

    const columns = useMemo(() => [
        {
            field: 'tipoDocumento',
            headerName: t('com.muralis.qcx.documento.tipoDocumento'),
            type: 'string',
            flex: 1.5,
            valueGetter: ({ row }) => row?.tipoDocumento,
        },
        {
            field: 'nome',
            headerName: t('com.muralis.qcx.nome'),
            type: 'string',
            flex: 1.5,
            valueGetter: ({ row }) => row?.nome,
        },
        {
            field: 'numero',
            headerName: t('com.muralis.qcx.numero'),
            type: 'string',
            flex: 1,
            valueGetter: ({ row }) => row?.numero,
        },
        {
            field: 'vencimento',
            headerName: t('com.muralis.qcx.vencimento'),
            type: 'string',
            flex: 1,
            valueGetter: ({ row }) => formatDate(row?.vencimento),
        },
        {
            field: 'notificar',
            headerName: 'Notificar',
            type: 'boolean',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            valueGetter: ({ row }) => row.notificar,
        },
        {
            field: 'actions',
            headerName: t('com.muralis.qcx.acoes.label'),
            flex: 0.5,
            align: 'center',
            headerAlign: 'center',
            renderCell: ({ row }) => (
                <>
                    <QCXPopupState popupId={`popup-menu-documentos-${row?.id}`}>
                        {(popupState) => (
                            <>
                                <Tooltip title={t('com.muralis.qcx.maisOpcoes')} arrow>
                                    <IconButton
                                        key={`btn-more-options-${row?.id}`}
                                        name={`btn-more-options-${row?.id}`}
                                        {...bindTrigger(popupState)}
                                    >
                                        <MoreHorizIcon color="secondary" size={20} />
                                    </IconButton>
                                </Tooltip>
                                <Menu {...bindMenu(popupState)}>
                                    <MenuItem
                                        key={`btn-consulta-${row?.id}`}
                                        onClick={(event) => {
                                            popupState.close(event);

                                            handleConsultDocument(row);
                                        }}
                                    >
                                        <QCXListItemIcon>
                                            <Pageview fontSize="small" />
                                        </QCXListItemIcon>
                                        <Typography
                                            variant='inherit'
                                            style={{
                                                fontSize: 12,
                                            }}
                                        >
                                            {t('com.muralis.qcx.acoes.consultar').toUpperCase()}
                                        </Typography>
                                    </MenuItem>
                                    <MenuItem
                                        key={`btn-excluir-${row?.id}`}
                                        onClick={(event) => {
                                            popupState.close(event);

                                            handleDeleteDocument(row.id);
                                        }}
                                    >
                                        <QCXListItemIcon>
                                            <Delete fontSize="small" />
                                        </QCXListItemIcon>
                                        <Typography
                                            variant='inherit'
                                            style={{
                                                fontSize: 12,
                                            }}
                                        >
                                            {t('com.muralis.qcx.acoes.excluir').toUpperCase()}
                                        </Typography>
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </QCXPopupState>
                </>
            ),
        },
    ], [t, handleConsultDocument, handleDeleteDocument, isDeleting]);

    return (
        <>
            <Grid container direction="row" style={{ marginTop: '0.7rem' }}>
                <QCXSimpleSelectManyPageTemplate
                    title={title}
                    tableColumns={columns}
                    tableData={documentsToDisplay}
                    filterProp={filterProp}
                    isLoading={isAlternativeLoading}
                    isConsult={isConsult}
                    handleSetSelected={handleChange}
                    selected={selectedDocuments}
                    dataGridProps={{
                        selectionModel: selectedDocuments,
                        onSelectionModelChange: handleSelectionDocumentsChange,
                        checkboxSelection: true,
                    }}
                />
            </Grid>

            <QCXCompoundDialog.Actions
                style={{ padding: '24px 24px 12px 24px', width: '100%' }}
            >
                <Grid container direction="column" alignItems="center">
                    {isSingleSelection && (
                        <Grid item xs={12} style={{ marginBottom: '12px' }}>
                            <QCXInfoAlert>
                                <Trans i18nKey="com.muralis.qcx.documento.selecioneMaisDocumentosBotoesDownloadEnvioMultiplo">
                                    <strong />
                                </Trans>
                            </QCXInfoAlert>
                        </Grid>
                    )}


                    <Grid item container xs={4} spacing={1}>
                        {/* Botão Imprimir */}
                        <Grid item xs="auto">
                            <QCXButton
                                id="button-follow-up-documentos-print"
                                key="button-follow-up-documentos-print"
                                color="secondary"
                                size="small"
                                tooltip={!isXsDownScreen}
                                tooltipDescription={t('com.muralis.qcx.acoes.imprimir')}
                                startIcon={isXsDownScreen && <PrintIcon />}
                                fullWidth
                                disabled
                            >
                                {!isXsDownScreen && <PrintIcon />}
                                {isXsDownScreen && t('com.muralis.qcx.acoes.imprimir')}
                            </QCXButton>
                        </Grid>

                        {/* Botão Download */}
                        <Grid item xs="auto">
                            <QCXButton
                                id="button-follow-up-documentos-download-selection"
                                key="button-follow-up-documentos-download-selection"
                                color="secondary"
                                size="small"
                                onClick={handleDownloadMultipleDocumentFromSelection}
                                tooltip={!isXsDownScreen}
                                tooltipDescription={t('com.muralis.qcx.documento.baixarDocumentosSelecionados')}
                                startIcon={isXsDownScreen && <FileDownloadIcon />}
                                fullWidth
                                disabled={!isMultipleSelection || downloading}
                            >
                                {!isXsDownScreen && <FileDownloadIcon />}
                                {isXsDownScreen && t('com.muralis.qcx.documento.baixarDocumentosSelecionados')}
                            </QCXButton>
                        </Grid>

                        {/* Botão Email */}
                        <Grid item xs="auto">
                            <QCXButton
                                id="button-follow-up-documentos-send-email"
                                key="button-follow-up-documentos-send-email"
                                color="secondary"
                                size="small"
                                onClick={handleOpenSendEmailWithMultipleDocumentFromSelection}
                                tooltip={!isXsDownScreen}
                                tooltipDescription={t('com.muralis.qcx.enviarPorEmail')}
                                startIcon={isXsDownScreen && <EmailIcon />}
                                fullWidth
                                disabled={!isMultipleSelection || downloading}
                            >
                                {!isXsDownScreen && <EmailIcon />}
                                {isXsDownScreen && t('com.muralis.qcx.enviarPorEmail')}
                            </QCXButton>
                        </Grid>

                        {/* Botão Adicionar Documento */}
                        <Grid item xs="auto">
                            <QCXButton
                                id="button-follow-up-documentos-upload-new-document"
                                key="button-follow-up-documentos-upload-new-document"
                                color="secondary"
                                size="small"
                                onClick={handleOpenModalNovoDocumento}
                                tooltip={!isXsDownScreen}
                                tooltipDescription={t('com.muralis.qcx.documento.adicionarNovoDocumento')}
                                startIcon={isXsDownScreen && <NoteAddIcon />}
                                fullWidth
                                disabled={isConsult}
                            >
                                {!isXsDownScreen && <NoteAddIcon />}
                                {isXsDownScreen && t('com.muralis.qcx.documento.adicionarNovoDocumento')}
                            </QCXButton>
                        </Grid>

                        {/* Botão Excluir em lote */}
                        <Grid item xs="auto">
                            <QCXButton
                                id="button-follow-up-documentos-send-email"
                                key="button-follow-up-documentos-send-email"
                                color="secondary"
                                size="small"
                                onClick={handleDeleteMultipleDocuments}
                                tooltip={!isXsDownScreen}
                                tooltipDescription={t('com.muralis.qcx.excluirDocumento')}
                                startIcon={isXsDownScreen && <Clear />}
                                fullWidth
                                disabled={!isAnySelection || isDeleting}
                            >
                                {!isXsDownScreen && <Clear />}
                                {isXsDownScreen && t('com.muralis.qcx.excluirDocumento')}
                            </QCXButton>
                        </Grid>
                    </Grid>
                </Grid>
            </QCXCompoundDialog.Actions>

            <QCXNovoDocumentoClientFormDialog
                parentModel={model}
                status={formDialogDocumentoFollowUpStatus}
                handleStatus={handleFormDialogDocumentoFollowUpStatus}
                onSuccess={handleRefreshDocuments}
                initialModel={documentoParaEdicao}
            />

            <QCXEmailDocumentoFollowUpFormDialog
                parentModel={model}
                status={formDialogDocumentoEmailFollowUpStatus}
                handleStatus={handleFormDialogDocumentoEmailFollowUpStatus}
                handleClearSelection={handleClearDocumentsSelection}
            />

            <QCXDocumentoClientViewerModal
                documento={documentoEmVisualizacao}
                open={!!documentoEmVisualizacao}
                onClose={handleCloseConsultModal}
                parentModel={model}
                onSuccess={handleRefreshDocuments}
            />
        </>
    );
}